import React from 'react';
import { Typography, Card, Statistic, Row, Col, Space, Button, Table, Tag, Empty } from 'antd';
import { 
    FileTextOutlined, 
    FormOutlined, 
    MessageOutlined, 
    PlusOutlined, 
    ArrowRightOutlined,
    ReadOutlined,
    PictureOutlined,
    NotificationOutlined,
    DatabaseOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '../../api/dashboard';
import { formsApi } from '../../api/forms';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

export const Dashboard: React.FC = () => {
    const navigate = useNavigate();

    const { data: statsResponse, isLoading: statsLoading } = useQuery({
        queryKey: ['dashboardStats'],
        queryFn: () => dashboardApi.getStats()
    });

    const { data: submissionsResponse, isLoading: submissionsLoading } = useQuery({
        queryKey: ['recentSubmissions'],
        queryFn: () => formsApi.getSubmissions({ pageNumber: 1, pageSize: 5 })
    });

    const stats = statsResponse?.data;
    const recentSubmissions = submissionsResponse?.data || [];

    const submissionColumns = [
        {
            title: 'Form',
            dataIndex: 'formName',
            key: 'formName',
            render: (text: string) => <Text strong>{text}</Text>
        },
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm')
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={status === 'New' ? 'blue' : 'green'}>
                    {status.toUpperCase()}
                </Tag>
            )
        },
        {
            title: 'Action',
            key: 'action',
            render: (_: any, record: any) => (
                <Button 
                    type="link" 
                    icon={<ArrowRightOutlined />} 
                    onClick={() => navigate(`/admin/forms/submissions?formId=${record.formDefinitionId}`)}
                >
                    View
                </Button>
            )
        }
    ];

    return (
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
            <div>
                <Title level={2} style={{ marginBottom: 0 }}>Dashboard</Title>
                <Text type="secondary">Welcome back to the Mosaik Administration Panel.</Text>
            </div>

            {/* Stats Cards */}
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                    <Card variant="borderless" loading={statsLoading}>
                        <Statistic
                            title="Total Courses"
                            value={stats?.totalCourses || 0}
                            prefix={<ReadOutlined style={{ color: '#1890ff' }} />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card variant="borderless" loading={statsLoading}>
                        <Statistic
                            title="Custom Forms"
                            value={stats?.totalForms || 0}
                            prefix={<FormOutlined style={{ color: '#52c41a' }} />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card variant="borderless" loading={statsLoading}>
                        <Statistic
                            title="Recent Submissions (24h)"
                            value={stats?.recentSubmissions || 0}
                            prefix={<FileTextOutlined style={{ color: '#faad14' }} />}
                            styles={{ content: { color: stats?.recentSubmissions > 0 ? '#faad14' : 'inherit' } }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card variant="borderless" loading={statsLoading}>
                        <Statistic
                            title="Recent Messages (24h)"
                            value={stats?.recentMessages || 0}
                            prefix={<MessageOutlined style={{ color: '#f5222d' }} />}
                            styles={{ content: { color: stats?.recentMessages > 0 ? '#f5222d' : 'inherit' } }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Notification Stats */}
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={12}>
                    <Card variant="borderless" loading={statsLoading} onClick={() => navigate('/admin/notifications')} style={{ cursor: 'pointer' }}>
                        <Statistic
                            title="Pending Notifications"
                            value={stats?.pendingNotifications || 0}
                            prefix={<ClockCircleOutlined style={{ color: '#1890ff' }} />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={12}>
                    <Card variant="borderless" loading={statsLoading} onClick={() => navigate('/admin/notifications')} style={{ cursor: 'pointer' }}>
                        <Statistic
                            title="Failed Notifications"
                            value={stats?.failedNotifications || 0}
                            prefix={<ExclamationCircleOutlined style={{ color: '#f5222d' }} />}
                            styles={{ content: { color: stats?.failedNotifications > 0 ? '#f5222d' : 'inherit' } }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                {/* Recent Activity Table */}
                <Col xs={24} lg={16}>
                    <Card 
                        title="Recent Form Submissions" 
                        extra={<Button type="link" onClick={() => navigate('/admin/forms')}>View All Forms</Button>}
                        variant="borderless"
                    >
                        <Table 
                            columns={submissionColumns} 
                            dataSource={recentSubmissions} 
                            pagination={false}
                            loading={submissionsLoading}
                            rowKey="id"
                            locale={{ emptyText: <Empty description="No recent submissions" /> }}
                        />
                    </Card>
                </Col>

                {/* Quick Actions */}
                <Col xs={24} lg={8}>
                    <Card title="Quick Actions" variant="borderless">
                        <Space orientation="vertical" style={{ width: '100%' }} size="middle">
                            <Button 
                                block 
                                icon={<PlusOutlined />} 
                                onClick={() => navigate('/admin/courses')}
                                size="large"
                            >
                                Manage Courses
                            </Button>
                            <Button 
                                block 
                                icon={<PlusOutlined />} 
                                onClick={() => navigate('/admin/forms')}
                                size="large"
                            >
                                Manage Forms
                            </Button>
                            <Button 
                                block 
                                icon={<PictureOutlined />} 
                                onClick={() => navigate('/admin/media')}
                                size="large"
                            >
                                Media Library
                            </Button>
                            <Button 
                                block 
                                icon={<MessageOutlined />} 
                                onClick={() => navigate('/admin/contact-messages')}
                                size="large"
                            >
                                Contact Messages
                                {stats?.recentMessages > 0 && (
                                    <Tag color="error" style={{ marginLeft: 8 }}>{stats.recentMessages} New</Tag>
                                )}
                            </Button>
                            <Button 
                                block 
                                icon={<NotificationOutlined />} 
                                onClick={() => navigate('/admin/notifications')}
                                size="large"
                            >
                                Notification Queue
                                {stats?.failedNotifications > 0 && (
                                    <Tag color="error" style={{ marginLeft: 8 }}>{stats.failedNotifications} Failed</Tag>
                                )}
                            </Button>
                            <Button 
                                block 
                                icon={<DatabaseOutlined />} 
                                href="/admin/hangfire"
                                target="_blank"
                                size="large"
                            >
                                Background Jobs (Hangfire)
                            </Button>
                        </Space>
                    </Card>
                </Col>
            </Row>
        </Space>
    );
};
